import { Component, ElementRef, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SelectionOption, StiiraError } from '@core/models';
import { ContactAdminForm, PostContactAdmin } from '@core/models/help-and-feedback/post-contact-admin.model';
import { ErrorService, LayoutService, NotificationService } from '@core/services';
import { HelpAndFeedbackService } from '@core/services/help-and-feedback.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { indicate, nameof, closeDialogOnEscape } from '@shared/helpers';
import { Subject, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';

@Component({
    selector: 'app-contact-admin',
    templateUrl: './contact-admin.component.html',
    styleUrls: ['./contact-admin.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContactAdminComponent implements OnInit {
  static readonly componentName = 'ContactAdminComponent';

  @ViewChild('imgFileInput') fileInput: ElementRef;
  @ViewChild('uploadButton', { read: ElementRef }) private uploadButton: ElementRef<HTMLButtonElement>;
  public fileToUpload: File;
  public form: FormGroup<ContactAdminForm>;
  public isSubmitting$: Subject<boolean>;

  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value) === JSON.stringify(this.formInitValues);
  }

  get formIsValid(): boolean {
    return this.form.valid;
  }

  get title(): FormControl<string> {
    return this.form.get(nameof<ContactAdminForm>('title')) as FormControl<string>;
  }

  get message(): FormControl<string> {
    return this.form.get(nameof<ContactAdminForm>('message')) as FormControl<string>;
  }

  get attachment(): FormControl<any> {
    return this.form.get(nameof<ContactAdminForm>('attachment')) as FormControl<any>;
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      admin: SelectionOption,
      sysText: any
    },
    private dialogRef: MatDialogRef<ContactAdminComponent>,
    private service: HelpAndFeedbackService,
    private errorService: ErrorService,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private notificationService: NotificationService
  ) {
    this.isSubmitting$ = new Subject();
    this.form = this.fb.group<ContactAdminForm>({
        title: this.fb.control(null, Validators.required),
        message: this.fb.control(null, Validators.required),
        attachment: this.fb.control(null)
      })
   }

   ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.cancel());

    this.formInitValues = { ...this.form.value };
  }

  onSubmit(): void {
    const dto: PostContactAdmin = {
      adminId: +this.data.admin.id,
      title: this.title.value,
      message: this.message.value,
      attachment: this.fileToUpload ?? null
    };

    // convert dto into formData to send to backend
    const formData = this.makeContactAdminFormData(dto);

    this.service.postContactAdmin(formData)
      .pipe(
        indicate(this.isSubmitting$),
        finalize(() => { })
      )
      .subscribe((res) => {
          this.dialogRef.close(res);
      },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
  }

  cancel(): void {
    this.dialogRef.close();
  }

  public clearFile(): void {
    // the pressed button goes with its file
    this.uploadButton.nativeElement.focus();
    this.fileToUpload = null;
    this.fileInput.nativeElement.value = "";
  }

  public handleFileInput(files: FileList): void {
    this.fileToUpload = files.item(0);
    this.notificationService.announceStatus(this.fileToUpload.name);
    this.attachment.setErrors({"msError": null});
    this.form.updateValueAndValidity();
  }

  private makeContactAdminFormData (dto: PostContactAdmin) {
    const formData = new FormData();
    formData.append(nameof<PostContactAdmin>('adminId'), dto.adminId.toString())
    formData.append(nameof<PostContactAdmin>('title'), dto.title)
    formData.append(nameof<PostContactAdmin>('message'), dto.message)
    formData.append(nameof<PostContactAdmin>('attachment'), dto.attachment)
    return formData;
  }
}
